import React, { useContext, useState, useCallback, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Password } from "primereact/password";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { Context } from "../../../index";
import { AppContext } from "../../../App";
import CustomLink from "../../Shared/CustomLink/CustomLink";
import PrivacyPolicyLink from "../../Shared/PrivacyPolicyLink";
import Logo from "../../../assets/mainLogo.svg";
import Background from "../../../assets/backgroundLogin.jpg";
import Loader from "../../Shared/Loader/Loader";
import LoadingPage from "../../../pages/Loading/LoadingPage";

interface CompanyData {
    name: string;
    description: string;
    logo: string;
    cover: string;
}

const defaultData: CompanyData = {
    name: "Development CS",
    description: "Слоган компании пользователя",
    logo: Logo,
    cover: Background,
};

const LoginForm = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [errors, setErrors] = useState({ email: "", password: "" });
    const [companyData, setCompanyData] = useState<CompanyData>();
    const { showToast } = useContext(AppContext);
    const { store } = useContext(Context);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const returnParam = searchParams.get("returnUrl");
    if (returnParam) {
        store.setReturnUrl(returnParam);
    }

    const isValidEmail = useCallback((email: string): boolean => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    }, []);

    const validateField = useCallback(
        (field: "email" | "password", value: string): string => {
            if (!value.trim()) {
                return field === "email" ? "Введите email" : "Введите пароль";
            }

            if (field === "email" && !isValidEmail(value)) {
                return "Некорректный email";
            }

            return "";
        },
        [isValidEmail]
    );

    const validateForm = useCallback((): boolean => {
        const newErrors = {
            email: validateField("email", email),
            password: validateField("password", password),
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some((error) => error);
    }, [email, password, validateField]);

    const handleLogin = useCallback(async () => {
        if (!validateForm()) return;

        try {
            await store.login(email, password);
            const returnUrl = store.getReturnUrl();
            navigate(returnUrl || "/");
        } catch (err) {
            if (isAxiosError(err) && err.response) {
                const message = err.response.data.message;
                const status = err.response.status;

                const errorMap: Record<number, () => void> = {
                    400: () => setErrors((prev) => ({ ...prev, password: message })),
                    404: () => setErrors((prev) => ({ ...prev, email: message })),
                    402: () => showToast(message),
                };

                if (status in errorMap) {
                    errorMap[status]();
                } else {
                    showToast(message);
                }
            } else {
                showToast("Произошла неизвестная ошибка");
            }
        }
    }, [email, password, store, navigate, validateForm, showToast]);
    const handleOauth = useCallback(
        async (access_token: string) => {
            if (!access_token) return;

            try {
                await store.loginOAuth(access_token);
                const returnUrl = store.getReturnUrl();
                navigate(returnUrl || "/");
            } catch (err) {
                if (isAxiosError(err)) {
                    showToast(err.response?.data.message);
                }
            }
        },
        [store, navigate, showToast]
    );

    const loadCompanyData = useCallback(async () => {
        try {
            const [settings, logo, cover] = await Promise.all([
                store.getPublicSettings(),
                store.commonService.getSystemLogo(),
                store.commonService.getSystemCover(),
            ]);

            const newCompanyData = {
                name: settings?.find((x) => x.paramName === "name")?.value || defaultData.name,
                description: settings?.find((x) => x.paramName === "description")?.value || defaultData.description,
                logo: logo.data.length ? logo.data : defaultData.logo,
                cover: cover.data.length ? cover.data : defaultData.cover,
            };

            setCompanyData(newCompanyData);
        } catch (error) {
            console.error("Failed to load company data:", error);
        }
    }, [store]);

    const initializeYandexOAuth = useCallback(() => {
        if (!(window as any).YaAuthSuggest) return;

        (window as any).YaAuthSuggest.init(
            {
                client_id: store.getOAuthId(),
                response_type: "token",
                redirect_uri: `${store.getExtUrl()}/login/token`,
            },
            store.getExtUrl(),
            {
                view: "button",
                parentId: "oauth_container",
                buttonSize: "l",
                buttonView: "additional",
                buttonTheme: "light",
                buttonBorderRadius: "6",
                buttonIcon: "ya",
            }
        )
            .then((x: any) => x.handler())
            .then((x: any) => handleOauth(x.access_token))
            .catch((error: any) => console.log("OAuth error:", error));
    }, [store, handleOauth]);

    const loadYandexScript = useCallback(() => {
        const script = document.createElement("script");
        script.src = "https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js";
        script.async = true;
        script.onload = initializeYandexOAuth;
        const oAuthContainer = document.querySelector(".login_in_with_Google.pointer_custom");
        oAuthContainer?.appendChild(script);
    }, [initializeYandexOAuth]);

    useEffect(() => {
        const initialize = async () => {
            await loadCompanyData();

            if (store.linkNewPerson && store.userWithStatus.email) {
                setEmail(store.userWithStatus.email);
            }

            loadYandexScript();
        };

        initialize();
    }, [loadCompanyData, loadYandexScript, store]);

    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
                handleLogin();
            }
        },
        [handleLogin]
    );

    const handleInputChange = useCallback(
        (field: "email" | "password") => (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            field === "email" ? setEmail(value) : setPassword(value);

            if (errors[field]) {
                setErrors((prev) => ({ ...prev, [field]: "" }));
            }
        },
        [errors]
    );

    const handleBlur = useCallback(
        (field: "email" | "password") => () => {
            const error = validateField(field, field === "email" ? email : password);
            setErrors((prev) => ({ ...prev, [field]: error }));
        },
        [email, password, validateField]
    );

    const formFields = useMemo(
        () => [
            {
                id: "login_email",
                type: "text" as const,
                label: "Email*",
                value: email,
                error: errors.email,
                onChange: handleInputChange("email"),
                onBlur: handleBlur("email"),
                placeholder: "email",
            },
            {
                id: "login_pass",
                type: "password" as const,
                label: "Пароль*",
                value: password,
                error: errors.password,
                onChange: handleInputChange("password"),
                onBlur: handleBlur("password"),
                placeholder: "********",
            },
        ],
        [email, password, errors, handleInputChange, handleBlur]
    );

    return (
        <>
            {!companyData && <LoadingPage />}
            <div className="form_main" onKeyDown={handleKeyPress}>
                <div className="form_container">
                    <div className="form_inner">
                        <div className="form_logo">
                            <img src={companyData?.logo} alt="Логотип компании" />
                        </div>

                        <div>
                            <h2 className="form_header form_text">Вход</h2>
                        </div>

                        <div id="oauth_container" className="login_in_with_Google pointer_custom">
                            {!store.getOAuthId() && <p className="p_gray p_14">Авторизация через Яндекс недоступна</p>}
                        </div>

                        <div className="login_in_with_Email form_flex_row pointer_custom">
                            <div></div>
                            <a href="#email">или через Email</a>
                            <div></div>
                        </div>

                        {formFields.map((field) => (
                            <div key={field.id} className={`input_form_login ${field.error ? "input_with_error" : ""}`}>
                                <label className="input_label" htmlFor={field.id}>
                                    {field.label}
                                </label>
                                {field.type === "password" ? (
                                    <Password
                                        id={field.id}
                                        value={field.value}
                                        onBlur={field.onBlur}
                                        onChange={field.onChange}
                                        feedback={false}
                                        toggleMask
                                        placeholder={field.placeholder}
                                    />
                                ) : (
                                    <InputText
                                        id={field.id}
                                        value={field.value}
                                        onBlur={field.onBlur}
                                        placeholder={field.placeholder}
                                        onChange={field.onChange}
                                    />
                                )}
                                {field.error && <p className="error_message">{field.error}</p>}
                            </div>
                        ))}

                        <div className="login_btn">
                            <Button label="Войти" onClick={handleLogin} />
                        </div>

                        <div className="form_flex_row form__margin-top">
                            <p className="p_gray p_14_login">Еще нет аккаунта?</p>
                            <p className="p_blue p_14 pointer_custom">
                                <CustomLink to="/register">Зарегистрируйтесь</CustomLink>
                            </p>
                        </div>

                        <div className="pointer_custom">
                            <p className="p_blue p_14">
                                <CustomLink to="/forgotPassword">Забыли пароль</CustomLink>?
                            </p>
                        </div>

                        <PrivacyPolicyLink />
                    </div>
                </div>

                <div className="form_background">
                    <div>
                        <h1 className="p_white h1_60">{companyData?.name}</h1>
                        <h3 className="p_white h3_36">{companyData?.description}</h3>
                    </div>
                    <img src={companyData?.cover} alt="Фон компании" />
                </div>
            </div>
        </>
    );
};

export default LoginForm;
